// src/pages/team/TeamSchedulesPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { scheduleStatusMap, scheduleTypeMap, getStatusMapping, getEntityId, formatDateTime, getDisplayName, refEntity } from '@/features/team/logic';
import { ScheduleFormDialog } from '@/features/team/ScheduleFormDialog';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';

export const TeamSchedulesPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t } = useI18n();

  const [schedules, setSchedules] = useState<any[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadSchedules = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListRoleSchedules(id);
      setSchedules(res.schedules?.schedules ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadSchedules();
  }, [id, loadSchedules]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handlePause = async (scheduleId: string) => {
    if (!id) return;
    try {
      await store.PauseRoleSchedule(id, scheduleId, { schedule: refEntity(scheduleId), reason: '' });
      showSuccess(t('team.schedulePaused'));
      loadSchedules();
    } catch { /* store handles */ }
  };

  const handleResume = async (scheduleId: string) => {
    if (!id) return;
    try {
      await store.ResumeRoleSchedule(id, scheduleId, { schedule: refEntity(scheduleId) });
      showSuccess(t('team.scheduleResumed'));
      loadSchedules();
    } catch { /* store handles */ }
  };

  const handleDelete = async (scheduleId: string) => {
    if (!id || !confirm(t('team.deleteScheduleConfirm'))) return;
    try {
      await store.DeleteRoleSchedule(id, scheduleId);
      showSuccess(t('team.scheduleDeleted'));
      loadSchedules();
    } catch { /* store handles */ }
  };

  const teamName = (store.currentItem as any)?.name || t('team.title');

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.nav.schedules')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="schedules" />


      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('team.schedulesTitle')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('team.schedulesDescription')}</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? t('common.cancel') : t('team.createSchedule')}
        </Button>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      {id && (
        <ScheduleFormDialog
          open={showCreate}
          teamId={id}
          onClose={() => setShowCreate(false)}
          onCreated={() => { showSuccess(t('team.scheduleCreated')); loadSchedules(); }}
        />
      )}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('common.type')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('common.currentAssignee')}</TableHead>
              <TableHead>{t('common.nextRotation')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell>
              </TableRow>
            ) : schedules.length === 0 ? (
              <TableEmpty colSpan={6} message={t('team.noSchedules')} />
            ) : (
              schedules.map((sch: any) => {
                const schId = getEntityId(sch);
                const status = sch.status || '';
                const isActive = status === 'ROLE_SCHEDULE_STATUS_ACTIVE';
                const isPaused = status === 'ROLE_SCHEDULE_STATUS_PAUSED';
                const sm = getStatusMapping(scheduleStatusMap, status);
                const typeLabel = scheduleTypeMap[sch.schedule_type] ? t(scheduleTypeMap[sch.schedule_type]) : sch.schedule_type;

                return (
                  <TableRow key={schId || Math.random()}>
                    <TableCell>
                      <Link to={`/teams/${id}/schedules/${schId}`} className="text-sm font-medium text-primary hover:underline">
                        {sch.name || '--'}
                      </Link>
                      {sch.description && <p className="text-xs text-muted-foreground">{sch.description}</p>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="primary" size="sm">{typeLabel}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={sm.variant} size="sm">
                        {t(sm.labelKey)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{getDisplayName(sch.current_assignee)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{formatDateTime(sch.next_rotation_at)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Link to={`/teams/${id}/schedules/${schId}`}>
                          <Button variant="ghost" size="sm">{t('common.view')}</Button>
                        </Link>
                        {isActive && (
                          <Button variant="ghost" size="sm" onClick={() => handlePause(schId)}>{t('common.pause')}</Button>
                        )}
                        {isPaused && (
                          <Button variant="ghost" size="sm" onClick={() => handleResume(schId)}>{t('common.resume')}</Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(schId)}>
                          {t('common.delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default TeamSchedulesPage;
